<template>
  <page>
    <review-provider
      v-model="review"
      :review-query="GetReviewForScreeningPage"
      subscribe-to-upload-updates
      :loading.sync="loading"
      @update:error="redirectToReviewsPage"
    />
    <template #header-bar>
      <page-header-bar
        v-if="!loading"
        :root-route="reviewPageRoute"
        class="pl-9"
        breadcrumb="Title &amp; Abstract Screening"
      >
        {{ review.title }}
        <template v-if="!loading" #actions>
          <div class="d-flex align-center">
            <settings-button :to="`/review/${review.id}/settings`" />
          </div>
        </template>
      </page-header-bar>
      <page-header-bar v-else>
        <v-skeleton-loader width="400" type="text" />
      </page-header-bar>
    </template>
    <template #content>
      <template v-if="hasReferences">
        <page-tabs
          v-model="selectedTabIndex"
          :tabs="tabs"
          use-tab-routes
        >
          <page-tab-panel class="pa-6">
            <screening-progress-insights-tab
              :review="review"
              :current-researcher="currentResearcher"
            />
          </page-tab-panel>
          <page-tab-panel>
            <screen-tab
              :review="review"
              :current-researcher="currentResearcher"
            />
          </page-tab-panel>
        </page-tabs>
      </template>
      <template v-else-if="!loading">
        <v-container class="d-flex justify-center fill-height">
          <upload-references-page-placeholder />
        </v-container>
      </template>
    </template>
    <upload-references-modal
      :review="review"
      @resolve-duplicates="redirectToDuplicatesPage"
    />
  </page>
</template>

<script>
import Page from '@/components/Common/Page';
import PageHeaderBar from '@/components/Common/PageHeaderBar';
import ReviewProvider from '@/components/Providers/ReviewProvider';
import PageTabs from '@/components/Common/PageTabs';
import PageTabPanel from '@/components/Common/PageTabPanel';
import ScreenTab from '@/components/ScreeningPageV2/ScreenTab/ScreenTab';
import ScreeningProgressInsightsTab from '@/components/ScreeningPageV2/ProgressTab/ScreeningProgressInsightsTab';
import UploadReferencesModal from '@/components/UploadReferences/UploadReferencesModal';
import UploadReferencesPagePlaceholder from '@/components/UploadReferences/UploadReferencesPagePlaceholder';
import SettingsButton from '@/components/Common/SettingsButton';
import { Researcher, Review } from '@/models';
import {
  GetCurrentResearcher,
  GetReviewForScreeningPage,
  GetReviewScreeningInsights,
} from '@/graphql';

export default {
  name: 'ScreeningPageV2',
  components: {
    Page,
    PageHeaderBar,
    ReviewProvider,
    PageTabs,
    PageTabPanel,
    ScreenTab,
    ScreeningProgressInsightsTab,
    UploadReferencesModal,
    UploadReferencesPagePlaceholder,
    SettingsButton,
  },
  data: () => ({
    review: null,
    loading: true,
    tabs: ['Progress & Insights', 'Screen'],
    selectedTabIndex: 0,
    currentResearcher: null,
    screeningInsights: null,
    GetReviewForScreeningPage,
  }),
  apollo: {
    currentResearcher: {
      query: GetCurrentResearcher,
      update: (data) => new Researcher(data.currentResearcher),
    },
    screeningInsights: {
      query: GetReviewScreeningInsights,
      variables() {
        return {
          id: this.$route.params.reviewId,
        };
      },
      skip() {
        return !this.review?.id;
      },
      update: (data) =>
        data.review ? new Review(data.review) : null,
    },
  },
  computed: {
    reviewPageRoute() {
      return `/review/${
        this.$route.params.reviewId ?? this.review.id
      }`;
    },
    hasReferences() {
      const totalReferences = this.review?.screeningProgressSummary.totalReferenceCount();
      return totalReferences > 0;
    },
  },
  watch: {
    // The provider replaces `review` with a fresh model on every page-query
    // emission (refetches included), and that model is built from a fragment
    // without the insights fields — so re-apply them each time either side
    // changes.
    review() {
      this.applyScreeningInsights();
    },
    screeningInsights() {
      this.applyScreeningInsights();
    },
  },
  methods: {
    applyScreeningInsights() {
      if (!this.review || !this.screeningInsights) return;
      this.review.activeJob = this.screeningInsights.activeJob;
      this.review.trainingStats = this.screeningInsights.trainingStats;
      this.review.anyRemainingRelevant = this.screeningInsights.anyRemainingRelevant;
      this.review.remainingRelevantAnalysis = this.screeningInsights.remainingRelevantAnalysis;
      this.review.mostRecentScreeningOutcomes = this.screeningInsights.mostRecentScreeningOutcomes;
    },
    redirectToReviewsPage() {
      this.$router.push('/');
    },
    redirectToDuplicatesPage() {
      this.$router.push(`/review/${this.review.id}/duplicates`);
    },
  },
};
</script>
